// Single-select employee overlay for "Assign to Staff" (Service Recovery).
// Same style as AssignEmployees overlay; no shift/leave validation.
import React, { useEffect, useMemo, useState } from 'react';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { Card, IconButton, PrimaryButton, ProfileImage, SearchBar } from '../ui';
import { employeeApi } from '../ShiftManagement/employeeApi';

const MemoizedProfileImage = React.memo(ProfileImage, (prevProps, nextProps) => {
  return (
    prevProps.userId === nextProps.userId &&
    prevProps.imagePath === nextProps.imagePath &&
    prevProps.userName === nextProps.userName
  );
});
MemoizedProfileImage.displayName = 'MemoizedProfileImage';

const EmployeeCard = React.memo(({ employee, isSelected, onSelect }) => {
  const profileImage = useMemo(() => (
    <MemoizedProfileImage
      key={`profile-${employee.user_id}`}
      userId={employee.user_id}
      userName={employee.display_name}
      imagePath={employee.image_path}
      size="md"
      showLoading={false}
    />
  ), [employee.user_id, employee.display_name, employee.image_path]);

  return (
    <div onClick={() => onSelect(employee)}>
      {isSelected ? (
        <Card.Gradient className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg">
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {profileImage}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-black text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.display_name}
                </div>
                <div className="w-full text-black text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.role} • ID: {employee.employee_id}
                </div>
              </div>
            </div>
            <div className="w-6 h-6 flex-shrink-0 ml-3 flex items-center justify-center bg-green-700 rounded-sm">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </Card.Gradient>
      ) : (
        <Card.Dark className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg">
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {profileImage}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-white text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.display_name}
                </div>
                <div className="w-full text-gray-300 text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.role} • ID: {employee.employee_id}
                </div>
              </div>
            </div>
            <div className="w-6 h-6 flex-shrink-0 ml-3 rounded-sm bg-zinc-100 border-[1.60px] border-black/30" />
          </div>
        </Card.Dark>
      )}
    </div>
  );
});

const SelectSingleEmployee = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Assign to Staff',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      setSelectedEmployee(null);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.length > 0) {
      const term = searchTerm.toLowerCase();
      const filtered = allEmployees.filter(
        (e) =>
          e.display_name?.toLowerCase().includes(term) ||
          e.role?.toLowerCase().includes(term) ||
          String(e.employee_id || '').toLowerCase().includes(term) ||
          String(e.user_id || '').toLowerCase().includes(term)
      );
      setFilteredEmployees(filtered);
      setSearchSuggestions(filtered.map((emp) => emp.display_name));
    } else {
      setFilteredEmployees(allEmployees);
      setSearchSuggestions([]);
    }
  }, [searchTerm, allEmployees]);

  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);
      let employeesData = await employeeApi.getAllUsers();
      const processedEmployees = await Promise.all(
        employeesData.map(async (employee) => {
          try {
            const imagePath = await employeeApi.getProfilePhoto(employee.user_id);
            if (imagePath && imagePath.trim() !== '') {
              const userId = employee.user_id || employee.employee_id;
              const cachedUrl = profileImageCache.get(userId);
              if (!cachedUrl) {
                if (imagePath.startsWith('http')) {
                  profileImageCache.set(userId, imagePath);
                } else {
                  profileImageCache.setS3Path(userId, imagePath);
                  userService.getS3PresignedUrl(imagePath).then((url) => {
                    if (url) profileImageCache.set(userId, url, imagePath);
                  }).catch(() => {});
                }
              }
            }
            return {
              ...employee,
              image_path: imagePath || '',
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || 'Staff',
              employee_id: employee.employee_id || employee.user_id,
            };
          } catch {
            return {
              ...employee,
              image_path: '',
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || 'Staff',
              employee_id: employee.employee_id || employee.user_id,
            };
          }
        })
      );
      setAllEmployees(processedEmployees);
      setFilteredEmployees(processedEmployees);
    } catch (err) {
      console.error('SelectSingleEmployee: Error fetching employees:', err);
      setError(err.message || 'Failed to load employees');
      setAllEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    const emp = allEmployees.find((e) => e.display_name === suggestion);
    if (emp) setFilteredEmployees([emp]);
    setSearchSuggestions([]);
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleConfirm = () => {
    if (selectedEmployee && typeof onSelect === 'function') {
      onSelect(selectedEmployee);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setFilteredEmployees(allEmployees);
    setSelectedEmployee(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="w-[464px] h-[682px] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden">
        <div className="w-96 left-[32px] top-[24px] absolute inline-flex justify-between items-center">
          <div className="flex flex-col">
            <h3 className={typography.h3} style={{ color: '#000000' }}>
              {title}
            </h3>
            {selectedEmployee && (
              <span className="text-sm text-gray-600">1 employee selected</span>
            )}
          </div>
          <IconButton
            onClick={handleClose}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          />
        </div>

        <div className="mt-20 px-8">
          <div className="mb-6">
            <SearchBar
              placeholder="Search for employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={() => {}}
              suggestions={searchSuggestions}
              onSuggestionClick={handleSuggestionClick}
              className="w-full"
            />
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {isLoading ? (
              <div className="text-center text-gray-500 py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2" />
                Loading employees...
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">
                <div className="mb-2">⚠️</div>
                <div className="text-sm">{error}</div>
                <button onClick={fetchEmployees} className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                  Retry
                </button>
              </div>
            ) : filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee, index) => (
                <EmployeeCard
                  key={employee.user_id || employee.employee_id || `emp-${index}`}
                  employee={employee}
                  isSelected={selectedEmployee && (selectedEmployee.user_id === employee.user_id || selectedEmployee.employee_id === employee.employee_id)}
                  onSelect={handleSelectEmployee}
                />
              ))
            ) : searchTerm.length > 0 ? (
              <div className="text-center text-gray-500 py-8">No employees found matching &quot;{searchTerm}&quot;</div>
            ) : (
              <div className="text-center text-gray-500 py-8">No employees available</div>
            )}
          </div>

          <div className="absolute bottom-6 left-8 right-8 flex gap-3 justify-center">
            <PrimaryButton onClick={handleConfirm} disabled={!selectedEmployee}>
              Confirm {selectedEmployee ? '(1)' : ''}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectSingleEmployee;
